import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';

// Import enhanced organization types for compatibility
import { 
  EnhancedOrgType,
  OrganisationContact,
  OrganisationSettings,
  OrganisationStaffRole 
} from 'src/@types/enhanced_organization';

// Enhanced Organizer interface with all required properties
export interface Organizer {
  id: string;
  name: string;
  description?: string;
  bio?: string; // Legacy compatibility
  type?: EnhancedOrgType;
  ownerId?: string;
  ownerid?: string; // Legacy compatibility
  language?: 'en' | 'fr';
  logo?: string;
  logoImage?: string; // Legacy compatibility
  coverImage?: string;
  contact?: OrganisationContact;
  settings?: OrganisationSettings;
  staffRoles?: OrganisationStaffRole[];
  followers?: number; // Legacy compatibility
  totalEvents?: number; // Legacy compatibility
  address?: string; // Legacy compatibility
  legalInfos?: string; // Legacy compatibility
  siren?: string;
  siret?: string;
  companyNumber?: number; // Legacy compatibility
  groupeTable?: any[]; // Legacy compatibility
  isDeleted?: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  passculture?: {
    environment?: string;
    offerer?: any;
    venues?: any[];
    venueCount?: number;
    lastSynced?: any;
    syncError?: string;
  };
  [key: string]: any;
}

interface FetchOrganizersResponse {
  organisers: Organizer[];
}

interface FetchOrganizerResponse {
  organizer: Organizer;
}

interface SyncPassCultureRequest {
  organizerId: string;
  environment?: string;
}

interface SyncPassCultureResponse {
  success: boolean;
  message?: string;
  offerer?: any;
  venues?: any[];
  environment?: string;
  error?: string;
}

interface CreateOrganizerRequest {
  name: string;
  description?: string;
  type?: EnhancedOrgType;
  ownerId: string;
  contact?: OrganisationContact;
  settings?: OrganisationSettings;
  [key: string]: any;
}

interface UpdateOrganizerRequest {
  id: string;
  updates: Partial<Omit<Organizer, 'id' | 'createdAt'>>;
}

// Create the Organizers API
export const OrganizersApi = createApi({
  reducerPath: 'organizersApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Organizer', 'Organizers'],
  endpoints: (builder) => ({
    // Get all organisers
    getOrganizers: builder.query<FetchOrganizersResponse, void>({
      async queryFn() {
        try {
          const organizersRef = collection(DB, 'organisers');
          const querySnapshot = await getDocs(organizersRef);
          
          const organisers: Organizer[] = [];
          querySnapshot.forEach((doc) => {
            organisers.push({ 
              id: doc.id, 
              ...doc.data()
            } as Organizer);
          });
          
          return { data: { organisers } };
        } catch (error: any) {
          console.error('Error fetching organisers:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organisers'
            }
          };
        }
      },
      providesTags: (result) => 
        result
          ? [
              ...result.organisers.map(({ id }) => ({ 
                type: 'Organizer' as const, 
                id 
              })),
              { type: 'Organizers', id: 'LIST' }
            ]
          : [{ type: 'Organizers', id: 'LIST' }]
    }),
    
    // Get a single organizer by ID
    getOrganizer: builder.query<FetchOrganizerResponse, string>({
      async queryFn(organizerId) {
        try {
          const organizerRef = doc(DB, 'organisers', organizerId);
          const docSnap = await getDoc(organizerRef);
          
          if (docSnap.exists()) {
            const organizer = { 
              id: docSnap.id, 
              ...docSnap.data() 
            } as Organizer;
            return { data: { organizer } };
          } else {
            return { 
              error: {
                status: 'NOT_FOUND',
                data: `Organizer with ID ${organizerId} not found`
              }
            };
          }
        } catch (error: any) {
          console.error('Error fetching organizer:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while fetching organizer'
            }
          };
        }
      },
      providesTags: (result, error, organizerId) => [
        { type: 'Organizer', id: organizerId }
      ]
    }),
    
    // Sync organizer with Pass Culture
    syncPassCulture: builder.mutation<SyncPassCultureResponse, SyncPassCultureRequest>({
      async queryFn(request) {
        try {
          // Using the 'ai-' prefix as requested
          const syncPassCultureFn = httpsCallable(FUNCTIONS, 'passculture-syncPassCultureOffererAndVenues');
          const result = await syncPassCultureFn(request);
          
          return { 
            data: result.data as SyncPassCultureResponse 
          };
        } catch (error: any) {
          console.error('Error syncing with Pass Culture:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while syncing with Pass Culture'
            }
          };
        }
      },
      invalidatesTags: (result, error, { organizerId }) => [
        { type: 'Organizer', id: organizerId }
      ]
    }),
    
    // Get organisers by search term
    searchOrganizers: builder.query<FetchOrganizersResponse, string>({
      async queryFn(searchTerm) {
        if (!searchTerm || searchTerm.trim().length < 2) {
          return { data: { organisers: [] } };
        }
        
        try {
          const organisers: Organizer[] = [];
          const organizersRef = collection(DB, 'organisers');
          
          // Fetch all organisers (Firestore doesn't support case-insensitive search natively)
          const querySnapshot = await getDocs(organizersRef);
          
          querySnapshot.forEach((doc) => {
            const data = doc.data() as Omit<Organizer, 'id'>;
            
            // Filter locally - case insensitive search on name
            if (data.name && data.name.toLowerCase().includes(searchTerm.toLowerCase())) {
              organisers.push({ id: doc.id, ...data } as Organizer);
            }
          });
          
          return { data: { organisers } };
        } catch (error: any) {
          console.error('Error searching organisers:', error);
          return { 
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while searching organisers'
            }
          };
        }
      },
      providesTags: [{ type: 'Organizers', id: 'SEARCH' }]
    }),

    // Create new organizer
    createOrganizer: builder.mutation<FetchOrganizerResponse, CreateOrganizerRequest>({
      async queryFn(organizerData) {
        try {
          const organizersRef = collection(DB, 'organisers');
          const now = serverTimestamp();
          
          const newOrganizerData = {
            ...organizerData,
            createdAt: now,
            updatedAt: now,
            isDeleted: false
          };
          
          const docRef = await addDoc(organizersRef, newOrganizerData);
          
          // Fetch the created organizer to return with server timestamps
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const organizer = {
              id: docSnap.id,
              ...docSnap.data()
            } as Organizer;
            
            return { data: { organizer } };
          } else {
            return {
              error: {
                status: 'CREATION_FAILED',
                data: 'Failed to retrieve created organizer'
              }
            };
          }
        } catch (error: any) {
          console.error('Error creating organizer:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while creating organizer'
            }
          };
        }
      },
      invalidatesTags: [{ type: 'Organizers', id: 'LIST' }]
    }),

    // Update organizer
    updateOrganizer: builder.mutation<FetchOrganizerResponse, UpdateOrganizerRequest>({
      async queryFn({ id, updates }) {
        try {
          const organizerRef = doc(DB, 'organisers', id);
          
          const updateData = {
            ...updates,
            updatedAt: serverTimestamp()
          };
          
          await updateDoc(organizerRef, updateData);
          
          // Fetch the updated organizer
          const docSnap = await getDoc(organizerRef);
          if (docSnap.exists()) {
            const organizer = {
              id: docSnap.id,
              ...docSnap.data()
            } as Organizer;
            
            return { data: { organizer } };
          } else {
            return {
              error: {
                status: 'NOT_FOUND',
                data: `Organizer with ID ${id} not found`
              }
            };
          }
        } catch (error: any) {
          console.error('Error updating organizer:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while updating organizer'
            }
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organizer', id },
        { type: 'Organizers', id: 'LIST' }
      ]
    }),

    // Delete organizer (soft delete)
    deleteOrganizer: builder.mutation<{ success: boolean }, string>({
      async queryFn(organizerId) {
        try {
          const organizerRef = doc(DB, 'organisers', organizerId);
          
          await updateDoc(organizerRef, {
            isDeleted: true,
            updatedAt: serverTimestamp()
          });
          
          return { data: { success: true } };
        } catch (error: any) {
          console.error('Error deleting organizer:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An error occurred while deleting organizer'
            }
          };
        }
      },
      invalidatesTags: (result, error, organizerId) => [
        { type: 'Organizer', id: organizerId },
        { type: 'Organizers', id: 'LIST' }
      ]
    }),
  }),
});

// Export hooks for use in functional components
export const {
  useGetOrganizersQuery,
  useGetOrganizerQuery,
  useSyncPassCultureMutation,
  useSearchOrganizersQuery,
  useCreateOrganizerMutation,
  useUpdateOrganizerMutation,
  useDeleteOrganizerMutation,
} = OrganizersApi;